// # packages
import * as React from 'react';
import { boundMethod } from 'autobind-decorator';
// # components
import { TabContent } from './Content';
import { Tab, iProps as iTabProps } from './Tab';

export interface iProps {
    /**
     * Specify which tab is currently active when using this component in controlled mode.
     * This should match the "id" prop of a tab.
     */
    activeTab?: number | string;
    /**
     * The tab that will be active when the component mounts when using this component
     * in uncontrolled mode.
     * This should match the "id" prop of a tab.
     */
    initialTab?: number | string;
    /**
     * Only accepts instances of <Tab> and <TabContent>
     * An error will be thrown if you attempt to pass anything else.
     */
    children: React.ReactNode | Array<React.ReactNode>;
    /**
     * Called when a tab is clicked.
     * Puts this component into "controlled mode" when provided.
     */
    onChange?: (selectedTab: number | string) => any;
}

interface iState {
    activeTab: number | string;
}

type ReactNodeWithType = React.ReactNode & {
    type: React.ComponentType<any>;
    props: Record<string, any>;
}

export class TabContainer extends React.Component<iProps, iState> {
    static displayName = 'TabContainer';

    /**
     * Only component with these displayName properties are allowed as children.
     */
    static validChildDisplayNames = [Tab.displayName, TabContent.displayName];

    state: iState = {
        activeTab: this.props.initialTab || this.props.activeTab,
    };

    componentWillReceiveProps(nextProps: iProps): void {
        // update activeTab in state if the prop has changed
        if (nextProps.activeTab && nextProps.activeTab !== this.state.activeTab) {
            this.setActiveTab(nextProps.activeTab);
        }
    }

    /**
     * Renders the content.
     *
     * @returns {React.ReactNode}
     */
    public render(): React.ReactNode {
        return (
            <div className="tabs">
                <div className="tabs__tab-bar">
                    {this.renderTabs()}
                    <div className="tabs__tab-bar-spacer" />
                </div>

                {this.renderActiveTabContent()}
            </div>
        );
    }

    /**
     * Renders the content(s) of the active tab.
     *
     * @returns {React.ReactNode[]}
     */
    protected renderActiveTabContent(): React.ReactNode[] {
        const { activeTab } = this.state;
        const children = this.validateChildren();

        // map over children and pull out any instances of <TabContent> where props.id === this.state.activeTab
        const $content: React.ReactNode[] = React.Children.map(children, (child: ReactNodeWithType) => {
            const isTab = child.type.displayName === TabContent.displayName;
            const isActive = child.props.id === activeTab;
            return isTab && isActive ? child : null;
        });

        return $content;
    }

    /**
     * Renders a list of tabs.
     *
     * @returns {React.ReactNode[]}
     */
    protected renderTabs(): React.ReactNode[] {
        const { onChange } = this.props;
        const { activeTab } = this.state;
        const children = this.validateChildren();

        // map over children and pull out any instances of <Tab>
        const $tabs: React.ReactNode[] = React.Children.map(children, (child: ReactNodeWithType) => {
            const isTab = child.type.displayName === Tab.displayName;
            const isActive = child.props.id === activeTab;

            // override the child props so we can mark one as active and intercept the click handler
            const overrideProps: Partial<iTabProps> = {
                active: isActive,
                onClick: onChange || this.setActiveTab,
            };

            return isTab ? React.cloneElement(child as React.ReactElement, overrideProps) : null;
        });

        return $tabs;
    }

    /**
     * Updates the active tab in this.state
     *
     * @param {number | string} activeTab
     * @returns {void}
     */
    @boundMethod
    protected setActiveTab(activeTab: number | string): void {
        this.setState({ activeTab });
    }

    /**
     * Throws an error if any child elements do not have a valid "displayName" property.
     *
     * This is currently the only way to limit allowed child components. We cannot do it at compile time
     * because JSX.Element (returned from calling React.ComponentType) returns <any> (I think!)
     *
     * This is a known issue:
     * @see https://github.com/microsoft/TypeScript/issues/13618
     *
     * @returns {React.ReactNode}
     */
    protected validateChildren(): React.ReactNode {
        const { children } = this.props;

        return React.Children.map(children, (child: ReactNodeWithType) => {
            // child could be null or an array or something
            if (!child || !child.type) { return; }

            const childName = child.type.displayName;
            const validNames = TabContainer.validChildDisplayNames;
            if (!validNames.includes(childName)) {
                throw new Error(`<TabContainer> can only contain child elements: ${validNames.join(', ')}. <${childName || child.type}> encountered.`);
            }

            return child;
        })
        // filter out any falsey values
        .filter(child => !!child);
    }
}
